import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Testimonial from '../../components/tools/Testimonial';
import ProductShowcase from '../../components/tools/ProductShowcase';
import GetStartedCta from '../../components/tools/GetStartedCta';
import CielBridge from '../../components/tools/CielBridge';
import NotFound from '../errors/NotFound';
import { getTools } from './toolsConfig';
import useAppURLs from '../../common/useAppURLs';
import { LIGHT_GRAY, PRIMARY_COLORS, WHITE } from '../../common/colors';
import PageMeta, { SITE } from '../../components/common/PageMeta';
import JsonLd from '../../components/common/JsonLd';

// Tool detail template shared by the four Webflow tools/*.html pages:
// hero (with a button that opens the tool, for a tool that has one), (three
// feature blocks), (CIEL bridge), (feature list), (product showcase),
// (testimonial), get-started CTA.
const HeroCta = ({ label, app, note }) => {
  const appURLs = useAppURLs();
  return (
    <Box sx={{ mt: 4 }}>
      <Button
        href={appURLs[app]}
        size="large"
        sx={{ backgroundColor: WHITE, color: 'surface.dark', px: 3, '&:hover': { backgroundColor: PRIMARY_COLORS['90'] } }}
      >
        {label}
      </Button>
      <Typography sx={{ color: PRIMARY_COLORS['90'], mt: 2 }}>
        {note}
      </Typography>
    </Box>
  );
};

const FeatureBlock = ({ title, text }) => (
  <Box>
    <Typography variant="h5" component="h3" sx={{ color: 'surface.dark', mb: 1.5 }}>
      {title}
    </Typography>
    <Typography sx={{ color: 'surface.contrastText' }}>
      {text}
    </Typography>
  </Box>
);

const FeatureListItem = ({ title, text }) => (
  <Box sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 2.5 }}>
    <Typography sx={{ color: 'surface.dark', fontWeight: 700, mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: 'surface.contrastText' }}>
      {text}
    </Typography>
  </Box>
);

const OrganizationsSection = ({ tagline, title, text, stats, link }) => (
  <Box sx={{ backgroundColor: PRIMARY_COLORS['10'] }}>
    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 6, alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" sx={{ color: PRIMARY_COLORS['80'], fontWeight: 600, mb: 1 }}>
            {tagline}
          </Typography>
          <Typography variant="h4" component="h2" sx={{ color: WHITE, mb: 2 }}>
            {title}
          </Typography>
          <Typography sx={{ color: PRIMARY_COLORS['90'], mb: 2 }}>
            {text}
          </Typography>
          <Link href={link.href} sx={{ color: WHITE, fontWeight: 600 }}>
            {link.label} →
          </Link>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4 }}>
          {stats.map(stat => (
            <Box key={stat.value}>
              <Typography sx={{ color: WHITE, fontSize: '3rem', fontWeight: 700, lineHeight: 1.1 }}>
                {stat.value}
              </Typography>
              <Typography variant="body2" sx={{ color: PRIMARY_COLORS['80'] }}>
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  </Box>
);

const ToolDetail = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const tool = getTools(t)[slug];

  if (!tool)
    return <NotFound />;

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('tools.eyebrow'), item: `${SITE}/tools` },
      { '@type': 'ListItem', position: 2, name: tool.name, item: `${SITE}/tools/${slug}` },
    ],
  };

  return (
    <Box>
      <PageMeta title={tool.name} description={tool.tagline} />
      <JsonLd data={breadcrumbJsonLd} />
      <Box sx={{ backgroundColor: 'primary.main' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 }, textAlign: 'center' }}>
          <Typography sx={{ color: PRIMARY_COLORS['90'], fontWeight: 600, mb: 1 }}>
            {t('tools.eyebrow')}
          </Typography>
          <Typography variant="h1" sx={{ color: WHITE, fontSize: { xs: '2.5rem', md: '3.25rem' }, mb: 2 }}>
            {tool.name}
          </Typography>
          <Typography sx={{ color: PRIMARY_COLORS['90'], fontSize: '1.125rem', maxWidth: 760, mx: 'auto' }}>
            {tool.tagline}
          </Typography>
          {/* Get started's primary button, repeated above the fold. */}
          {tool.hero && <HeroCta {...tool.getStarted.buttons[0]} note={tool.hero.note} />}
        </Container>
      </Box>
      {tool.features && (
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 4, md: 6 } }}>
            {tool.features.map(feature => <FeatureBlock key={feature.title} {...feature} />)}
          </Box>
        </Container>
      )}
      {tool.bridge && <CielBridge {...tool.bridge} />}
      {tool.featureList && (
        <Box sx={{ borderTop: `1px solid ${LIGHT_GRAY}` }}>
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 4 }}>
              {tool.featureList.map(feature => <FeatureListItem key={feature.title} {...feature} />)}
            </Box>
          </Container>
        </Box>
      )}
      {tool.showcase && <ProductShowcase {...tool.showcase} />}
      {tool.testimonial && <Testimonial {...tool.testimonial} />}
      {tool.organizations && <OrganizationsSection {...tool.organizations} />}
      <GetStartedCta {...tool.getStarted} />
    </Box>
  );
};

export default ToolDetail;
